import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { AngularEditorConfig } from '@kolkov/angular-editor';

@Component({
  selector: 'app-template-details',
  templateUrl: './template-details.component.html',
  styleUrls: ['./template-details.component.scss']
})

export class TemplateDetailsComponent implements OnInit, OnChanges {
  @Input() templateDetails;
  @Input() templateType: string = '';
  @Input() logo = '';
  @Input() mobileNumber = '91-74104 10123';
  @Input() cardHeading: { title: string, previewTitle: string };
  @Input() index = null;
  @Input() showPreviewOnly: boolean = false; // Add this to control preview mode

  @Input() companyName: string = ''; // Add company name input
  @Input() assessmentData: any = null; // Add assessment data input
  @Input() profile: any = null; // Parent-provided profile to avoid re-fetching
  dateObj: number = Date.now();

  config: AngularEditorConfig = {
    height: 'auto',
    minHeight: '0',
    width: 'auto',
    minWidth: '0',
    showToolbar: false
  };
  placeholderDummies: {
    [key: string]: any;
  } = {};

  constructor() { }

  ngOnInit(): void {
    this.initializePlaceholders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize placeholders when inputs change
    if (changes['companyName'] || changes['mobileNumber'] || changes['profile']) {
      this.initializePlaceholders();
    }
  }

  private initializePlaceholders(): void {
    // Get company name from input or fallback to provided profile
    const res = this.profile || {};
    const companyName = this.companyName || res.name || res.companyName || res.company || 'Your Company';

    // Get the actual user email from multiple sources with better error handling
    let userEmail = 'hr@company.com'; // Default fallback
    try {
      // First try: Get from localStorage currentUser
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (currentUser && currentUser.email) {
        userEmail = currentUser.email;
      }
      // Second try: Get from profile data if available
      else if (res && res.email) {
        userEmail = res.email;
      }
      // Third try: Get from profile data admins array if current user is there
      else if (res && res.admins && Array.isArray(res.admins)) {
        const currentUserFromStorage = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const matchingAdmin = res.admins.find(admin => admin.email === currentUserFromStorage.email);
        if (matchingAdmin && matchingAdmin.email) {
          userEmail = matchingAdmin.email;
        }
      }
    } catch (error) {
      // Error parsing currentUser from localStorage - using default email
    }

    this.placeholderDummies = {
      // Backend variables - double curly brackets format (backend regex: \\{{${key}\\}})
      '{{company_name}}': companyName,
      '{{COMPANY_NAME}}': companyName,
      '{{enterprise_name}}': companyName,
      '{{ENTERPRISE_NAME}}': companyName,
      '{{org_name}}': companyName,
      '{{company}}': companyName,
      '{{COMPANY}}': companyName,

      '{{candidate_name}}': 'John Smith',
      '{{CANDIDATE_NAME}}': 'John Smith',
      '{{Candidate_Name}}': 'John Smith',

      '{{job_title}}': 'Job Role',
      '{{JOB_TITLE}}': 'Job Role',
      '{{jobTitle}}': 'Job Role',
      '{{role}}': 'Job Role',
      '{{ROLE}}': 'Job Role',

      '{{assessmentName}}': 'Assessment Name',
      '{{assessment_name}}': 'Assessment Name',
      '{{ASSESSMENT_NAME}}': 'Assessment Name',

      '{{link}}': 'https://assessment.evaltrix.com/start?token=abc123xyz789',
      '{{LINK}}': 'https://assessment.evaltrix.com/start?token=abc123xyz789',

      '{{company_phone}}': this.mobileNumber,
      '{{COMPANY_PHONE}}': this.mobileNumber,
      '{{MOBILE_NUMBER}}': this.mobileNumber,
      '{{mobile_number}}': this.mobileNumber,
      '{{PHONE_NUMBER}}': this.mobileNumber,
      '{{phone_number}}': this.mobileNumber,

      '{{company_email}}': userEmail,
      '{{COMPANY_EMAIL}}': userEmail,

      '{{duration}}': 'N mins',
      '{{DURATION}}': 'N mins',

      '{{TOKEN}}': 'abc123xyz789',
      '{{token}}': 'abc123xyz789'
    };

    // If assessment data is provided (e.g., from settings preview), override with actual values
    if (this.assessmentData) {
      const roleName = this.assessmentData.roleName || this.assessmentData.jobTitle || 'Job Role';
      const assessmentName = this.assessmentData.name || 'Assessment Name';
      // Support either a flat duration or metaData-based module durations
      const totalDuration = this.computeDurationFromAssessment(this.assessmentData);

      this.placeholderDummies = {
        ...this.placeholderDummies,
        '{{job_title}}': roleName,
        '{{JOB_TITLE}}': roleName,
        '{{jobTitle}}': roleName,
        '{{role}}': roleName,
        '{{ROLE}}': roleName,
        '{{assessmentName}}': assessmentName,
        '{{ASSESSMENT_NAME}}': assessmentName,
        '{{duration}}': this.formatDuration(totalDuration),
        '{{DURATION}}': this.formatDuration(totalDuration)
      };
    }
  }

  private computeDurationFromAssessment(assessmentData: any): number {
    // Prefer direct duration when provided
    if (typeof assessmentData?.duration === 'number') {
      return assessmentData.duration;
    }
    // Otherwise, try summing metaData module durations
    const meta = assessmentData?.metaData || {};
    const coding = Number(meta?.['CODING']?.moduleDuration || 0);
    const mcq = Number(meta?.['MULTIPLE_CHOICE']?.moduleDuration || 0);
    const openEnded = Number(meta?.['OPEN_ENDED']?.moduleDuration || 0);
    const sum = coding + mcq + openEnded;
    return sum || 0;
  }

  private formatDuration(value: number): string {
    const n = Number(value) || 0;
    const unit = n === 1 ? 'min' : 'mins';
    return `${n} ${unit}`;
  }

  // Determine if WhatsApp template should display logo
  hasWhatsAppLogo(): boolean {
    if (this.templateType !== 'whatsapp') { return false; }
    // Prefer explicit backend flag when present
    if (typeof this.templateDetails?.logo === 'boolean') { return this.templateDetails.logo; }
    // Fallback: infer from template name (no logo if name mentions no logo/image)
    const name: string = String(this.templateDetails?.templateName || '').toLowerCase();
    return !(name.includes('no logo') || name.includes('no image'));
  }
}
